import { useEffect, useMemo, useState } from 'react';

import useOffersUI from 'api/useOffersUI';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

export interface IOfferDesignOption {
  value: string;
  label: string;
  externalId: string;
}

interface IUseOfferDesignOptionsProps {
  publisherId: string;
  offerDesignType: OfferDesignTypes;
  defaultExternalId?: string;
  offerId?: string;
}

interface IUseOfferDesignOptionsReturn {
  offerDesignOptions: IOfferDesignOption[];
  defaultOfferDesignId: string;
  getOfferUI: ReturnType<typeof useOffersUI>['getOfferUI'];
  getOffersUI: ReturnType<typeof useOffersUI>['getOffersUI'];
  currentOfferUI: string;
  setCurrentOfferUI: (id: string) => void;
}

/**
 * Hook that centralizes offer design fetching, filtering, option mapping,
 * auto-refetch on design change, and default design selection for new offers.
 *
 * @example
 * const { offerDesignOptions, getOfferUI, setCurrentOfferUI } = useOfferDesignOptions({
 *   publisherId,
 *   offerDesignType: OfferDesignTypes.Bundle,
 *   offerId
 * });
 */
export const useOfferDesignOptions = ({
  publisherId,
  offerDesignType,
  defaultExternalId,
  offerId
}: IUseOfferDesignOptionsProps): IUseOfferDesignOptionsReturn => {
  const [currentOfferUI, setCurrentOfferUI] = useState('');
  const { getOfferUI, getOffersUI } = useOffersUI(publisherId, currentOfferUI);

  const offerDesignOptions: IOfferDesignOption[] = useMemo((): IOfferDesignOption[] => {
    if (!getOffersUI.data?.result) {
      return [];
    }

    return getOffersUI.data.result
      .filter(
        (oi: { offerUiType?: string }): boolean =>
          oi.offerUiType === offerDesignType
      )
      .map(
        (offerUI: {
          _id: string;
          name: string;
          externalId?: string;
        }): IOfferDesignOption => ({
          value: offerUI._id,
          label: offerUI.name,
          externalId: offerUI.externalId || ''
        })
      );
  }, [getOffersUI.data?.result, offerDesignType]);

  const defaultOfferDesignId: string = useMemo((): string => {
    if (!offerDesignOptions.length) return '';

    if (defaultExternalId) {
      const match = offerDesignOptions.find(
        (option: IOfferDesignOption): boolean =>
          option.externalId === defaultExternalId
      );
      if (match) return match.value;
    }

    // Fall back to the first available option
    return offerDesignOptions[0].value;
  }, [offerDesignOptions, defaultExternalId]);

  // Set default design when creating a new offer (no offerId)
  useEffect(() => {
    if (!offerId && defaultOfferDesignId) {
      setCurrentOfferUI(defaultOfferDesignId);
    }
  }, [defaultOfferDesignId, offerId]);

  return {
    offerDesignOptions,
    defaultOfferDesignId,
    getOfferUI,
    getOffersUI,
    currentOfferUI,
    setCurrentOfferUI
  };
};
